import { ToastContainer } from "react-toastify";
import { useLocalStorage } from "usehooks-ts";
import { QueryClient, QueryClientProvider } from "react-query";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, styled, ThemeProvider, Toolbar } from "@mui/material";
import { loadMessages, LocalizationProvider } from "@progress/kendo-react-intl";
import MdmAppBar from "./Layout/Header/MdmAppBar";
import RouteContents from "./RouteContents";
import EnsureAuthentication from "./Auth/EnsureAuthentication";
import AxiosRequestInterceptor from "./Auth/AxiosRequestInterceptor";

import "./Theme/dhl-custom-theme.css";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import MdmBreadcrumbs from "./Layout/Breadcrumbs/MdmBreadcrumbs";
import { DhlTheme } from "./Theme/DhlMuiTheme";
import kendoMessages from "./Theme/Messages/kendo_messages.en-US.json";
import PersistentDrawerLeft from "./Layout/Navigation/MuiDrawer";

loadMessages(kendoMessages, "en-US");
const drawerWidth = 240;
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: `${drawerWidth}px`,
  }),
}));

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });
  const [navExpanded, setNavExpanded] = useLocalStorage<boolean>("NavExpanded", true);

  const handleHamburgerClick = () => {
    setNavExpanded(!navExpanded);
  };

  return (
    <LocalizationProvider language="en-US">
      <ThemeProvider theme={DhlTheme}>
        <QueryClientProvider client={queryClient}>
          <CssBaseline />
          <div className="App">
            <MdmAppBar onToggleButtonClick={handleHamburgerClick} />
            <EnsureAuthentication>
              {/* TODO - Request Interceptors are re-added on each rerender - refactor this to only occur once */}
              <AxiosRequestInterceptor>
                <>
                  <PersistentDrawerLeft open={navExpanded} />
                  <Box component="main" sx={{ flexGrow: 1, bgcolor: "background.default", p: 3 }}>
                    <Toolbar />
                    <Main open={navExpanded}>
                      <MdmBreadcrumbs />
                      <RouteContents />
                    </Main>
                  </Box>
                </>
              </AxiosRequestInterceptor>
            </EnsureAuthentication>
            <ToastContainer />
          </div>
        </QueryClientProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
